import React, {FC, useState, useEffect} from 'react'
import {KTCard, KTCardBody} from '../../../_metronic/helpers'
import {KTIcon} from '../../../_metronic/helpers'
import {get_dashboard_data} from './api/dashboardApi'
import {toast} from 'react-toastify'
import {LoadingProgressBar} from '../projects/components/LoadingProgressBar'
import { permissionCheck } from '../../helper-functions/PermissionCheck'
import { useNavigate } from 'react-router-dom'
import AssetDownloadModal from '../digital-assets/components/AssetDownloadModal'
import { Asset, AssetCategory } from '../digital-assets/types/assetTypes'
import { Card, Badge, Button } from 'react-bootstrap'

interface DashboardStatsProps {
  data: any;
  isLoading: boolean;
  canViewAssets: boolean;
  canViewProjects: boolean;
}

const DashboardStats: FC<DashboardStatsProps> = ({ data, isLoading, canViewAssets, canViewProjects }) => {
  const stats = {
    totalAssets: data?.total_assets || 0,
    activeAssets: data?.active_assets || 0,
    totalProjects: data?.total_projects || 0,
    activeProjects: data?.active_projects || 0,
    totalCategories: data?.total_categories || 0,
  }

  const statCardsRaw = [
    {
      title: 'Total Assets',
      value: stats.totalAssets,
      icon: 'picture',
      color: 'primary',
      permission: canViewAssets,
    },
    {
      title: 'Active Assets',
      value: stats.activeAssets,
      icon: 'check-circle',
      color: 'success',
      permission: canViewAssets,
    },
    {
      title: 'Total Projects',
      value: stats.totalProjects,
      icon: 'abstract-26',
      color: 'info',
      permission: canViewProjects,
    },
    {
      title: 'Total Categories',
      value: stats.totalCategories,
      icon: 'category',
      color: 'danger',
      permission: canViewAssets,
    }
  ]

  const visibleCards = statCardsRaw.filter(card => card.permission)

  if (isLoading) {
    const skeletonCount = visibleCards.length || ((canViewAssets || canViewProjects) ? 4 : 0)

    if (skeletonCount === 0) {
      return (
        <div className='row g-5 g-xl-8 mb-5'>
          <div className='col-12'>
            <div className='alert alert-warning mb-0'>
              You do not have permission to view dashboard statistics.
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className='row g-5 g-xl-8 mb-5'>
        {Array.from({length: skeletonCount}).map((_, index) => (
          <div key={`dashboard-stat-skeleton-${index}`} className='col-xl-3 col-lg-6 col-md-6'>
            <KTCard className='card-xl-stretch mb-xl-8'>
              <KTCardBody className='d-flex flex-column p-6'>
                <div className='d-flex align-items-center mb-4'>
                  <div className='symbol symbol-50px me-3'>
                    <div className='symbol-label bg-light placeholder-wave'></div>
                  </div>
                  <div>
                    <div className='placeholder placeholder-lg bg-light w-100px mb-2'></div>
                    <div className='placeholder placeholder-sm bg-light w-80px'></div>
                  </div>
                </div>
              </KTCardBody>
            </KTCard>
          </div>
        ))}
      </div>
    )
  }

  if (visibleCards.length === 0) {
    return (
      <div className='row g-5 g-xl-8 mb-5'>
        <div className='col-12'>
          <div className='alert alert-warning mb-0'>
            You do not have permission to view dashboard statistics.
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='row g-5 g-xl-8 mb-5'>
      {visibleCards.map((card, index) => (
        <div key={index} className='col-xl-3 col-lg-6 col-md-6'>
          <KTCard className='card-xl-stretch mb-xl-8'>
            <KTCardBody className='d-flex flex-column p-6'>
              <div className='d-flex align-items-center mb-4'>
                <div className={`symbol symbol-50px me-3 bg-light-${card.color}`}>
                  <KTIcon iconName={card.icon} className={`fs-2x text-${card.color}`} />
                </div>
                <div>
                  <div className='fs-2 fw-bolder text-gray-900'>{card.value}</div>
                  <div className='fs-7 text-muted'>{card.title}</div>
                </div>
              </div>
            </KTCardBody>
          </KTCard>
        </div>
      ))}
    </div>
  )
}


// Helper function to get thumbnail URL from file path
const getThumbnailUrl = (filePath: string | null | undefined): string | null => {
  if (!filePath) return null;
  
  // If already a full URL, return as is
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
    return filePath;
  }
  
  // Construct full URL from base URL
  let apiBaseUrl = process.env.REACT_APP_API_URL || '';
  // Remove /api suffix if present, but keep the base domain
  apiBaseUrl = apiBaseUrl.replace(/\/api\/?$/, '');
  // Ensure base URL doesn't end with slash
  apiBaseUrl = apiBaseUrl.replace(/\/$/, '');
  
  // Handle file path - remove leading slash if present, then add it back
  const cleanPath = filePath.replace(/^\//, '');
  
  return `${apiBaseUrl}/${cleanPath}`;
};

// Helper function to get category badge variant
const getCategoryBadgeVariant = (categoryName: string | undefined): string => {
  if (!categoryName) return 'secondary';
  const name = categoryName.toLowerCase();
  if (name.includes('video template')) return 'primary';
  if (name.includes('stock video') || name.includes('videos')) return 'success';
  if (name.includes('audio')) return 'warning';
  return 'info';
};

// Helper function to format category label
const getCategoryLabel = (categoryName: string | undefined): string => {
  if (!categoryName) return 'Unknown';
  return categoryName;
};

// Helper function to format date
const formatDate = (dateString?: string): string => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString();
};

interface TopAssetsProps {
  data: any;
  isLoading: boolean;
  onDownload?: (asset: Asset) => void;
  canDownload?: boolean;
  canView?: boolean;
}

const TopAssets: FC<TopAssetsProps> = ({ data, isLoading, onDownload, canDownload = false, canView = false }) => {
  const navigate = useNavigate()
  const assets = Array.isArray(data?.top_5_assets) ? data.top_5_assets : []
  
  // Map API response to display format with thumbnail URL
  const topAssets = assets.map((a: any) => {
    // Get thumbnail URL from primary_thumbnail
    let thumbnailUrl: string | null = null;
    if (a.primary_thumbnail?.file_path) {
      thumbnailUrl = getThumbnailUrl(a.primary_thumbnail.file_path);
    } else if (a.thumbnails && Array.isArray(a.thumbnails) && a.thumbnails.length > 0) {
      const primaryThumb = a.thumbnails.find((t: any) => t.is_primary === 1 || t.is_primary === true);
      if (primaryThumb?.file_path) {
        thumbnailUrl = getThumbnailUrl(primaryThumb.file_path);
      } else if (a.thumbnails[0]?.file_path) {
        thumbnailUrl = getThumbnailUrl(a.thumbnails[0].file_path);
      }
    }

    return {
    id: a.id?.toString() || '',
    name: a.upload_name || a.name || '',
    description: a.description || '',
    status: a.status === 1 ? 'Ready' : 'Pending',
    asset_file_path: a.asset_file_path || null,
    created_at: a.created_at || '',
    category: a.category,
    tags: a.tags || [],
      thumbnailUrl: thumbnailUrl,
    zip_file_url: a.asset_file_path || null,
      platform: a.platform,
      licenses_count: a.licenses_count || 0
    }
  })

  const handleView = (assetId: string) => {
    if (canView && assetId) {
      navigate(`/digital-assets/view/${assetId}`)
    }
  }

  const handleDownloadClick = (e: React.MouseEvent, asset: any) => {
                              e.stopPropagation()
                              if (onDownload) {
                                const assetData: Asset = {
                                  id: parseInt(asset.id),
                                  name: asset.name,
                                  category: asset.category?.category_name 
                                    ? (asset.category.category_name.toLowerCase().includes('video template') 
                                        ? AssetCategory.VIDEO_TEMPLATES
                                        : asset.category.category_name.toLowerCase().includes('stock video')
                                        ? AssetCategory.STOCK_VIDEOS
                                        : asset.category.category_name.toLowerCase().includes('audio')
                                        ? AssetCategory.AUDIOS
                                        : AssetCategory.GRAPHICS)
                                    : AssetCategory.GRAPHICS,
                                  description: asset.description || '',
                                  tags: asset.tags?.map((t: any) => t.tag_title || t) || [],
        thumbnails: asset.thumbnailUrl ? [asset.thumbnailUrl] : [],
                                  zip_file_url: asset.asset_file_path || '',
                                  licenses: [],
                                  metadata: {
                                    size: 0,
                                    file_type: 'zip',
                                    mime_type: 'application/zip',
                                  },
                                  source_platform: asset.platform?.platform_name as any,
                                  uploaded_at: asset.created_at || '',
                                  status: 'completed',
                                }
                                onDownload(assetData)
                              }
  }

  return (
    <div className='col-xl-12'>
      <KTCard className='card-xl-stretch mb-xl-8'>
        <KTCardBody className='d-flex flex-column p-6'>
          <div className='d-flex align-items-center mb-4'>
            <h3 className='fs-4 fw-bolder text-gray-900 mb-0'>Top Assets</h3>
          </div>
          
          {isLoading ? (
            <div className='row g-4'>
              {[1, 2, 3, 4, 5, 6].map((index) => (
                <div key={index} className='col-md-3 col-lg-3'>
                  <Card className='h-100 shadow-sm border-0'>
                    <div className='placeholder placeholder-lg bg-light w-100' style={{ height: '220px' }}></div>
                    <Card.Body>
                      <div className='placeholder placeholder-lg bg-light w-75 mb-2'></div>
                      <div className='placeholder placeholder-sm bg-light w-100'></div>
                    </Card.Body>
                  </Card>
                </div>
              ))}
            </div>
          ) : topAssets.length === 0 ? (
            <div className='text-center py-5'>
              <KTIcon iconName='picture' className='fs-3x text-muted mb-3' />
              <p className='text-muted fs-6'>No assets found</p>
            </div>
          ) : (
            <div className='row g-4'>
              {topAssets.map((asset, index) => (
                <div key={asset.id || index} className='col-md-3 col-lg-3'>
                  <Card 
                    className='h-100 shadow-sm border-0 card-hover' 
                    style={{ transition: 'all 0.3s ease', cursor: canView ? 'pointer' : 'default' }}
                    onClick={canView ? () => handleView(asset.id) : undefined}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                    }}
                  >
                    <div className='position-relative'>
                      {asset.thumbnailUrl ? (
                        <Card.Img
                          variant='top'
                          src={asset.thumbnailUrl}
                          style={{ height: '220px', objectFit: 'cover' }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23e0e0e0" width="400" height="300"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" font-size="18" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                          }}
                        />
                      ) : (
                        <div 
                          className='d-flex align-items-center justify-content-center bg-light'
                          style={{ height: '220px' }}
                        >
                          <KTIcon iconName='picture' className='fs-1 text-muted' />
                        </div>
                      )}
                      {/* Status Badge */}
                      <div className='position-absolute top-0 end-0 m-2'>
                        {asset.asset_file_path && asset.asset_file_path !== null && asset.asset_file_path.trim() !== '' ? (
                          <Badge bg='success' className='px-2 py-1'>
                            <KTIcon iconName='check-circle' className='me-1 fs-7' />
                            Ready
                          </Badge>
                        ) : (
                          <Badge bg='warning' className='px-2 py-1'>
                            <KTIcon iconName='clock' className='me-1 fs-7' />
                            Pending
                          </Badge>
                        )}
                      </div>
                      {/* Category Badge */}
                      {asset.category?.category_name && (
                        <div className='position-absolute top-0 start-0 m-2'>
                          <Badge bg={getCategoryBadgeVariant(asset.category.category_name)} className='px-2 py-1'>
                            {getCategoryLabel(asset.category.category_name)}
                          </Badge>
                        </div>
                      )}
                    </div>
                    <Card.Body className='d-flex flex-column p-3'>
                      <Card.Title className='fs-6 fw-bold mb-2 text-truncate' title={asset.name}>
                        {asset.name}
                      </Card.Title>
                      
                      {/* Description Preview */}
                      {asset.description && (
                        <Card.Text className='text-muted small mb-2' style={{ 
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          minHeight: '40px'
                        }}>
                          {asset.description}
                        </Card.Text>
                      )}

                      {/* Platform Badge */}
                      {asset.platform?.platform_name && (
                        <div className='mb-2'>
                          <Badge bg='info' className='px-2 py-1'>
                            <KTIcon iconName='link' className='me-1 fs-8' />
                            {asset.platform.platform_name}
                          </Badge>
                        </div>
                      )}

                      {/* Licenses Count */}
                      {asset.licenses_count > 0 && (
                        <div className='mb-2'>
                          <div className='d-flex align-items-center text-muted small'>
                            <KTIcon iconName='file-down' className='me-1 fs-7' />
                            <span>{asset.licenses_count} license{asset.licenses_count !== 1 ? 's' : ''}</span>
                          </div>
                        </div>
                      )}

                      {/* Date */}
                      <div className='mb-2'>
                        <div className='d-flex align-items-center text-muted small'>
                          <KTIcon iconName='calendar' className='me-1 fs-7' />
                          <span>{formatDate(asset.created_at)}</span>
                        </div>
                      </div>

                      {/* Tags */}
                      {asset.tags && asset.tags.length > 0 && (
                        <div className='mb-2'>
                          {asset.tags.slice(0, 2).map((tag: any, tagIndex: number) => (
                            <Badge key={tagIndex} bg='secondary' className='me-1 mb-1' style={{ fontSize: '10px' }}>
                              {tag.tag_title || tag}
                            </Badge>
                          ))}
                          {asset.tags.length > 2 && (
                            <Badge bg='secondary' style={{ fontSize: '10px' }}>
                              +{asset.tags.length - 2}
                            </Badge>
                          )}
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className='d-flex gap-1 mt-auto pt-2 border-top' onClick={(e) => e.stopPropagation()}>
                        {canView && (
                          <Button
                            variant='primary'
                            size='sm'
                            onClick={(e) => {
                              e.stopPropagation();
                              handleView(asset.id);
                            }}
                            className='flex-fill'
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                          >
                            <KTIcon iconName='eye' className='me-1 fs-7' />
                            View
                          </Button>
                        )}
                        {canDownload && asset.asset_file_path && asset.asset_file_path.trim() !== '' && (
                          <Button
                            variant='info'
                            size='sm'
                            onClick={(e) => handleDownloadClick(e, asset)}
                            title='Download Asset'
                            className='flex-fill'
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                          >
                            <KTIcon iconName='file-down' className='me-1 fs-7' />
                            Download
                          </Button>
                    )}
                  </div>
                    </Card.Body>
                  </Card>
                </div>
              ))}
            </div>
          )}
        </KTCardBody>
      </KTCard>
    </div>
  )
}

interface RecentAssetsProps {
  data: any;
  isLoading: boolean;
  onDownload?: (asset: Asset) => void;
  canDownload?: boolean;
  canView?: boolean;
}

interface LatestProjectsProps {
  data: any;
  isLoading: boolean;
}

const LatestProjects: FC<LatestProjectsProps> = ({ data, isLoading }) => {
  const navigate = useNavigate()
  const projects = Array.isArray(data?.latest_5_projects) ? data.latest_5_projects : []
  // Map API response to display format
  const latestProjects = projects.map((p: any) => ({
    id: p.id?.toString() || '',
    name: p.project_name || p.name || '',
    description: p.description || '',
    status: p.status === 1 ? 'Active' : 'Inactive',
    createdAt: p.created_at || new Date().toISOString(),
    upload_count: p.upload_count || 0
  }))

  return (
    <div className='col-xl-12'>
      <KTCard className='card-xl-stretch mb-xl-8'>
        <KTCardBody className='d-flex flex-column p-6'>
          <div className='d-flex align-items-center mb-4'>
            <h3 className='fs-4 fw-bolder text-gray-900 mb-0'>Latest Projects</h3>
          </div>
          
          {isLoading ? (
            <div className='d-flex flex-column gap-4'>
              {[1, 2, 3, 4, 5].map((index) => (
                <div key={index} className='d-flex align-items-center'>
                  <div className='symbol symbol-40px me-3'>
                    <div className='symbol-label bg-light placeholder-wave'></div>
                  </div>
                  <div className='d-flex flex-column flex-grow-1'>
                    <div className='placeholder placeholder-lg bg-light w-150px mb-2'></div>
                    <div className='placeholder placeholder-sm bg-light w-200px'></div>
                  </div>
                  <div className='d-flex flex-column text-end'>
                    <div className='placeholder placeholder-lg bg-light w-60px h-25px rounded mb-1'></div>
                  </div>
                </div>
              ))}
            </div>
          ) : latestProjects.length === 0 ? (
            <div className='text-center py-5'>
              <KTIcon iconName='abstract-26' className='fs-3x text-muted mb-3' />
              <p className='text-muted fs-6'>No projects found</p>
            </div>
          ) : (
            <div className='d-flex flex-column gap-4'>
              {latestProjects.map((project, index) => (
                <div 
                  key={project.id || index} 
                  className='d-flex align-items-center'
                >
                  <div className='symbol symbol-40px me-3'>
                    <div className='symbol-label bg-light-info'>
                      <span className='fs-6 fw-bolder text-info'>{index + 1}</span>
                    </div>
                  </div>
                  <div className='d-flex flex-column flex-grow-1'>
                    <div className='fs-6 fw-bolder text-gray-900'>{project.name}</div>
                    {project.description && (
                      <div className='fs-7 text-muted' style={{
                        maxWidth: '200px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }} title={project.description}>
                        {project.description}
                      </div>
                    )}
                    <div className='fs-7 text-muted mt-1'>
                      Upload Count: <a href={`/projects/detail/${project.id}`} target="_blank" rel="noopener noreferrer" className='fw-bold text-gray-900 text-decoration-none'>{project.upload_count}</a>
                    </div>
                  </div>
                  <div className='d-flex align-items-center gap-3'>
                    <span className={`badge ${project.status === 'Active' ? 'badge-light-success' : 'badge-light-danger'} fw-bold`}>
                      {project.status}
                    </span>
                    {project.id && (
                      <button
                        className='btn btn-sm btn-light-primary'
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/projects/detail/${project.id}`)
                        }}
                        title='View Project'
                      >
                        <KTIcon iconName='eye' className='fs-5' />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </KTCardBody>
      </KTCard>
    </div>
  )
}

const RecentAssets: FC<RecentAssetsProps> = ({ data, isLoading, onDownload, canDownload = false, canView = false }) => {
  const navigate = useNavigate()
  const assets = Array.isArray(data?.latest_5_assets) ? data.latest_5_assets : []
  
  // Map API response to display format with thumbnail URL
  const recentAssets = assets.map((a: any) => {
    // Get thumbnail URL from primary_thumbnail
    let thumbnailUrl: string | null = null;
    if (a.primary_thumbnail?.file_path) {
      thumbnailUrl = getThumbnailUrl(a.primary_thumbnail.file_path);
    } else if (a.thumbnails && Array.isArray(a.thumbnails) && a.thumbnails.length > 0) {
      const primaryThumb = a.thumbnails.find((t: any) => t.is_primary === 1 || t.is_primary === true);
      if (primaryThumb?.file_path) {
        thumbnailUrl = getThumbnailUrl(primaryThumb.file_path);
      } else if (a.thumbnails[0]?.file_path) {
        thumbnailUrl = getThumbnailUrl(a.thumbnails[0].file_path);
      }
    }

    return {
    id: a.id?.toString() || '',
    name: a.upload_name || a.name || '',
    description: a.description || '',
    status: a.asset_file_path ? 'Ready' : 'Pending',
    createdAt: a.created_at || new Date().toISOString(),
    asset_file_path: a.asset_file_path || null,
    category: a.category,
    tags: a.tags || [],
      thumbnailUrl: thumbnailUrl,
    platform: a.platform
    }
  })

  const handleView = (assetId: string) => {
    if (canView && assetId) {
      navigate(`/digital-assets/view/${assetId}`)
    }
  }

  const handleDownloadClick = (e: React.MouseEvent, asset: any) => {
    e.stopPropagation()
                                  if (onDownload) {
                                    const assetData: Asset = {
                                      id: parseInt(asset.id),
                                      name: asset.name,
                                      category: asset.category?.category_name 
                                        ? (asset.category.category_name.toLowerCase().includes('video template') 
                                            ? AssetCategory.VIDEO_TEMPLATES
                                            : asset.category.category_name.toLowerCase().includes('stock video')
                                            ? AssetCategory.STOCK_VIDEOS
                                            : asset.category.category_name.toLowerCase().includes('audio')
                                            ? AssetCategory.AUDIOS
                                            : AssetCategory.GRAPHICS)
                                        : AssetCategory.GRAPHICS,
                                      description: asset.description || '',
                                      tags: asset.tags?.map((t: any) => t.tag_title || t) || [],
        thumbnails: asset.thumbnailUrl ? [asset.thumbnailUrl] : [],
                                      zip_file_url: asset.asset_file_path || '',
                                      licenses: [],
                                      metadata: {
                                        size: 0,
                                        file_type: 'zip',
                                        mime_type: 'application/zip',
                                      },
                                      source_platform: asset.platform?.platform_name as any,
                                      uploaded_at: asset.createdAt || '',
                                      status: 'completed',
                                    }
                                    onDownload(assetData)
                                  }
  }

  return (
    <div className='col-xl-12'>
      <KTCard className='card-xl-stretch mb-xl-8'>
        <KTCardBody className='d-flex flex-column p-6'>
          <div className='d-flex align-items-center mb-4'>
            <h3 className='fs-4 fw-bolder text-gray-900 mb-0'>Recent Assets</h3>
          </div>
          
          {isLoading ? (
            <div className='row g-4'>
              {[1, 2, 3, 4, 5, 6].map((index) => (
                <div key={index} className='col-md-3 col-lg-3'>
                  <Card className='h-100 shadow-sm border-0'>
                    <div className='placeholder placeholder-lg bg-light w-100' style={{ height: '220px' }}></div>
                    <Card.Body>
                      <div className='placeholder placeholder-lg bg-light w-75 mb-2'></div>
                      <div className='placeholder placeholder-sm bg-light w-100'></div>
                    </Card.Body>
                  </Card>
                </div>
              ))}
            </div>
          ) : recentAssets.length === 0 ? (
            <div className='text-center py-5'>
              <KTIcon iconName='picture' className='fs-3x text-muted mb-3' />
              <p className='text-muted fs-6'>No recent assets found</p>
            </div>
          ) : (
            <div className='row g-4'>
              {recentAssets.map((asset, index) => (
                <div key={asset.id || index} className='col-md-3 col-lg-3'>
                  <Card 
                    className='h-100 shadow-sm border-0 card-hover' 
                    style={{ transition: 'all 0.3s ease', cursor: canView ? 'pointer' : 'default' }}
                    onClick={canView ? () => handleView(asset.id) : undefined}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                    }}
                  >
                    <div className='position-relative'>
                      {asset.thumbnailUrl ? (
                        <Card.Img
                          variant='top'
                          src={asset.thumbnailUrl}
                          style={{ height: '220px', objectFit: 'cover' }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23e0e0e0" width="400" height="300"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" font-size="18" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                          }}
                        />
                      ) : (
                        <div 
                          className='d-flex align-items-center justify-content-center bg-light'
                          style={{ height: '220px' }}
                        >
                          <KTIcon iconName='picture' className='fs-1 text-muted' />
                        </div>
                      )}
                      {/* Status Badge */}
                      <div className='position-absolute top-0 end-0 m-2'>
                        {asset.asset_file_path && asset.asset_file_path !== null && asset.asset_file_path.trim() !== '' ? (
                          <Badge bg='success' className='px-2 py-1'>
                            <KTIcon iconName='check-circle' className='me-1 fs-7' />
                            Ready
                          </Badge>
                        ) : (
                          <Badge bg='warning' className='px-2 py-1'>
                            <KTIcon iconName='clock' className='me-1 fs-7' />
                            Pending
                          </Badge>
                        )}
                      </div>
                      {/* Category Badge */}
                      {asset.category?.category_name && (
                        <div className='position-absolute top-0 start-0 m-2'>
                          <Badge bg={getCategoryBadgeVariant(asset.category.category_name)} className='px-2 py-1'>
                            {getCategoryLabel(asset.category.category_name)}
                          </Badge>
                        </div>
                      )}
                    </div>
                    <Card.Body className='d-flex flex-column p-3'>
                      <Card.Title className='fs-6 fw-bold mb-2 text-truncate' title={asset.name}>
                        {asset.name}
                      </Card.Title>
                      
                      {/* Description Preview */}
                      {asset.description && (
                        <Card.Text className='text-muted small mb-2' style={{ 
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          minHeight: '40px'
                        }}>
                          {asset.description}
                        </Card.Text>
                      )}

                      {/* Platform Badge */}
                      {asset.platform?.platform_name && (
                        <div className='mb-2'>
                          <Badge bg='info' className='px-2 py-1'>
                            <KTIcon iconName='link' className='me-1 fs-8' />
                            {asset.platform.platform_name}
                          </Badge>
                        </div>
                      )}

                      {/* Date */}
                      <div className='mb-2'>
                        <div className='d-flex align-items-center text-muted small'>
                          <KTIcon iconName='calendar' className='me-1 fs-7' />
                          <span>{formatDate(asset.createdAt)}</span>
                        </div>
                      </div>

                      {/* Tags */}
                      {asset.tags && asset.tags.length > 0 && (
                        <div className='mb-2'>
                          {asset.tags.slice(0, 2).map((tag: any, tagIndex: number) => (
                            <Badge key={tagIndex} bg='secondary' className='me-1 mb-1' style={{ fontSize: '10px' }}>
                              {tag.tag_title || tag}
                            </Badge>
                          ))}
                          {asset.tags.length > 2 && (
                            <Badge bg='secondary' style={{ fontSize: '10px' }}>
                              +{asset.tags.length - 2}
                            </Badge>
                            )}
                          </div>
                      )}

                      {/* Action Buttons */}
                      <div className='d-flex gap-1 mt-auto pt-2 border-top' onClick={(e) => e.stopPropagation()}>
                        {canView && (
                          <Button
                            variant='primary'
                            size='sm'
                            onClick={(e) => {
                              e.stopPropagation();
                              handleView(asset.id);
                            }}
                            className='flex-fill'
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                          >
                            <KTIcon iconName='eye' className='me-1 fs-7' />
                            View
                          </Button>
                        )}
                        {canDownload && asset.asset_file_path && asset.asset_file_path.trim() !== '' && (
                          <Button
                            variant='info'
                            size='sm'
                            onClick={(e) => handleDownloadClick(e, asset)}
                            title='Download Asset'
                            className='flex-fill'
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                          >
                            <KTIcon iconName='file-down' className='me-1 fs-7' />
                            Download
                          </Button>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              ))}
            </div>
          )}
        </KTCardBody>
      </KTCard>
    </div>
  )
}

const DashboardPage: FC = () => {
  const [dashboardData, setDashboardData] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [showDownloadModal, setShowDownloadModal] = useState(false)
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null)
  const canViewAssets = Boolean(permissionCheck('upload-view'))
  const canViewProjects = Boolean(permissionCheck('project-view'))
  const canDownloadAsset = Boolean(permissionCheck('download-view'))

  useEffect(() => {
    if (canViewAssets || canViewProjects) {
      fetchDashboardData()
    } else {
      setIsLoading(false)
    }
  }, [canViewAssets, canViewProjects])

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true)
      const response = await get_dashboard_data()
      
      if (response?.status && response?.data) {
        setDashboardData(response.data)
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
      toast.error('Failed to load dashboard data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDownload = (asset: Asset) => {
    // Check if asset file exists
    if (!asset.zip_file_url || asset.zip_file_url === null || asset.zip_file_url.trim() === '') {
      toast.warning('File is not uploaded yet. Please upload the file first before downloading.')
      return
    }
    setSelectedAsset(asset)
    setShowDownloadModal(true)
  }

  return (
    <div className='container-fluid'>
      {/* Loading Progress Bar */}
      <LoadingProgressBar isLoading={isLoading} />
      
      {/* Welcome Section */}
      <div className='row mb-5'>
        <div className='col-12'>
          <div className='card bg-light-primary'>
            <div className='card-body p-6'>
              <div className='d-flex align-items-center'>
                <div className='symbol symbol-60px me-4'>
                  <div className='symbol-label bg-primary text-white fs-2 fw-bolder'>
                    D
                  </div>
                </div>
                <div>
                  <h1 className='fs-2 fw-bolder text-primary mb-1'>Welcome to Digital Assets MS Dashboard</h1>
                  <p className='text-muted fs-6 mb-0'>Manage your digital assets, projects, and track performance</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <DashboardStats
        data={dashboardData}
        isLoading={isLoading}
        canViewAssets={canViewAssets}
        canViewProjects={canViewProjects}
      />

      {/* Recent Assets */}
      {canViewAssets && (
        <div className='row g-5 g-xl-8 mb-5'>
          <RecentAssets 
            data={dashboardData} 
            isLoading={isLoading} 
            onDownload={handleDownload}
            canDownload={canDownloadAsset}
            canView={canViewAssets}
          />
        </div>
      )}

      {/* Top Assets */}
      {canViewAssets && (
      <div className='row g-5 g-xl-8 mb-5'>
          <TopAssets 
            data={dashboardData} 
            isLoading={isLoading} 
            onDownload={handleDownload}
            canDownload={canDownloadAsset}
            canView={canViewAssets}
          />
        </div>
        )}

      {/* Latest Projects Row */}
        {canViewProjects && (
        <div className='row g-5 g-xl-8 mb-5'>
          <LatestProjects data={dashboardData} isLoading={isLoading} />
        </div>
        )}

      {/* Download Modal */}
      <AssetDownloadModal
        show={showDownloadModal}
        onHide={() => {
          setShowDownloadModal(false)
          setSelectedAsset(null)
        }}
        asset={selectedAsset}
      />
    </div>
  )
}

export {DashboardPage}



